// NOVA — the ACA & PPO Nurture Agent's operator dashboard. Shows every
// active journey, what touch is next, and gives the operator the human-
// override pause/resume controls.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Sparkles, Pause, Play, ChevronRight, RefreshCcw, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(body?.error || `HTTP ${res.status}`);
    e.status = res.status; e.body = body;
    throw e;
  }
  return body;
}

const STATUS_FILTERS = [
  { key: 'all',          label: 'all',          tone: 'slate'    },
  { key: 'active',       label: 'active',       tone: 'sky'      },
  { key: 'engaged',      label: 'engaged',      tone: 'emerald'  },
  { key: 'consent_sent', label: 'consent sent', tone: 'amber'    },
  { key: 'enrolled',     label: 'enrolled',     tone: 'emerald'  },
  { key: 'paused',       label: 'paused',       tone: 'slate'    },
  { key: 'capped',       label: 'capped',       tone: 'rose'     },
  { key: 'opted_out',    label: 'opted out',    tone: 'rose'     },
];

const TOUCH_LABELS = [
  'Instant response',
  'Subsidy hook',
  'Education',
  'Pick a plan',
  'Send consent',
  'Breakup',
  'Complete',
];

function statusChip(status, paused) {
  if (paused) return chip('Paused', 'slate');
  const meta = {
    active:       ['Active',       'sky'    ],
    engaged:      ['Engaged',      'emerald'],
    consent_sent: ['Consent sent', 'amber'  ],
    enrolled:     ['Enrolled',     'emerald'],
    opted_out:    ['Opted out',    'rose'   ],
    capped:       ['Capped',       'rose'   ],
    escalated:    ['Escalated',    'amber'  ],
    paused:       ['Paused',       'slate'  ],
  }[status] || [status, 'slate'];
  return chip(meta[0], meta[1]);
}

function chip(label, tone) {
  const tones = {
    sky:     'bg-sky-100 text-sky-800 border-sky-200',
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    amber:   'bg-amber-100 text-amber-800 border-amber-200',
    rose:    'bg-rose-100 text-rose-800 border-rose-200',
    slate:   'bg-slate-100 text-slate-700 border-slate-200',
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${tones[tone] || tones.slate}`}>{label}</span>;
}

function fmtAgo(iso) {
  if (!iso) return '—';
  const t = Date.parse(iso); if (!Number.isFinite(t)) return '—';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1)    return 'just now';
  if (mins < 60)   return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function NovaNurtureView({ currentUser, onGotoTab = () => {}, showToast = () => {} }) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [status,  setStatus]  = React.useState('active');
  // Per-tenant master switch — readable + writable through user_state
  // so the operator never has to know about the underlying JSON key.
  // null = still loading, true/false = known state.
  const [novaEnabled, setNovaEnabled] = React.useState(null);
  const [enabledBusy, setEnabledBusy] = React.useState(false);

  const loadEnabled = React.useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const { data } = await supabase.from('user_state')
        .select('value').eq('user_id', currentUser.id).eq('key', 'cfg_settings_v1').maybeSingle();
      const v = data?.value || {};
      setNovaEnabled(v.novaNurtureEnabled === true);
    } catch { setNovaEnabled(false); }
  }, [currentUser?.id]);

  const toggleNovaEnabled = async () => {
    if (!currentUser?.id) return;
    setEnabledBusy(true);
    try {
      const { data: existing } = await supabase.from('user_state')
        .select('value').eq('user_id', currentUser.id).eq('key', 'cfg_settings_v1').maybeSingle();
      const cur = (existing?.value && typeof existing.value === 'object') ? existing.value : {};
      const next = { ...cur, novaNurtureEnabled: !novaEnabled };
      const { error } = await supabase.from('user_state')
        .upsert({ user_id: currentUser.id, key: 'cfg_settings_v1', value: next }, { onConflict: 'user_id,key' });
      if (error) { showToast(`Couldn't save: ${error.message}`, 'error'); return; }
      setNovaEnabled(!novaEnabled);
      showToast(!novaEnabled ? 'NOVA is now ON for your tenant.' : 'NOVA is now OFF — automated journeys paused.', 'success');
    } finally {
      setEnabledBusy(false);
    }
  };

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const r = await apiFetch(`/api/nova/list?status=${encodeURIComponent(status)}`);
      setData(r);
    } catch (err) {
      showToast(`NOVA load failed: ${err.message}`, 'error');
    } finally { setLoading(false); }
  }, [currentUser?.id, status, showToast]);

  React.useEffect(() => { load(); loadEnabled(); }, [load, loadEnabled]);

  const togglePause = async (j) => {
    try {
      await apiFetch('/api/nova/pause', {
        method: 'POST',
        body: JSON.stringify({ journey_id: j.id, paused: !j.paused }),
      });
      showToast(j.paused ? 'Journey resumed.' : 'Journey paused.', 'success');
      load();
    } catch (err) {
      showToast(`Toggle failed: ${err.message}`, 'error');
    }
  };

  if (loading || !data) {
    return (
      <div className="max-w-none mx-auto px-4 py-4">
        <div className="text-sm text-slate-500">Loading NOVA…</div>
      </div>
    );
  }

  const summary = data.summary || {};
  const journeys = data.journeys || [];

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">
      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-900 to-slate-950 text-white rounded-xl px-5 py-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-violet-200/80 flex items-center gap-2">
              <Sparkles size={11}/> NOVA — ACA &amp; PPO Nurture Agent
            </div>
            <div className="text-lg font-bold mt-1">Warm leads to enrolled — without losing them to silence.</div>
            <div className="text-xs text-violet-200/70 mt-0.5">
              Fear-removal cadence · 6-touch cap · STOP-respecting · Hands the licensed moment to you.
            </div>
          </div>
          <div className="text-right text-violet-100/70 text-xs">
            Snapshot {fmtAgo(data.generated_at)}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
          <KpiTile label="Active"        value={summary.active || 0}        tone="sky"/>
          <KpiTile label="Engaged"       value={summary.engaged || 0}       tone="emerald"/>
          <KpiTile label="Consent sent"  value={summary.consent_sent || 0}  tone="amber"/>
          <KpiTile label="Enrolled"      value={summary.enrolled || 0}      tone="emerald"/>
          <KpiTile label="Capped / opt-out" value={(summary.capped || 0) + (summary.opted_out || 0)} tone="rose"/>
        </div>
      </div>

      {/* Master switch + compliance reminder.
          The technical "settings.novaNurtureEnabled = true" instruction
          confused non-developers. Replaced with a clear master toggle
          + a plain-English explanation. */}
      <div className={`rounded-xl px-4 py-3 flex items-start gap-3 ${
        novaEnabled === true
          ? 'bg-emerald-50 border border-emerald-200'
          : 'bg-amber-50 border border-amber-200'
      }`}>
        {novaEnabled === true
          ? <CheckCircle2 size={16} className="text-emerald-700 mt-0.5 shrink-0"/>
          : <AlertCircle  size={16} className="text-amber-700 mt-0.5 shrink-0"/>}
        <div className="flex-1 text-xs leading-relaxed">
          <div className={novaEnabled === true ? 'text-emerald-900' : 'text-amber-900'}>
            <strong>NOVA automation is currently {novaEnabled === true ? 'ON' : 'OFF'}.</strong>{' '}
            {novaEnabled === true
              ? <>Touches dispatch automatically per the 6-touch cap. NOVA still honors STOP keywords instantly and per-journey pause toggles below.</>
              : <>Journeys are tracked but no automated touches will dispatch. Flip the switch when you're ready to let NOVA work the queue.</>}
          </div>
        </div>
        <button
          onClick={toggleNovaEnabled}
          disabled={novaEnabled === null || enabledBusy}
          className={`shrink-0 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full disabled:opacity-50 ${
            novaEnabled === true
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-amber-600 hover:bg-amber-700 text-white'
          }`}
          title={novaEnabled === true ? 'Click to turn NOVA OFF' : 'Click to turn NOVA ON'}
        >
          {novaEnabled === null ? '…' : novaEnabled ? 'Turn OFF' : 'Turn ON'}
        </button>
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_FILTERS.map(f => {
          const count = f.key === 'all' ? (summary.total || 0) : (summary[f.key] || 0);
          const active = status === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setStatus(f.key)}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {f.label} · {count}
            </button>
          );
        })}
        <button onClick={load} className="ml-auto text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <RefreshCcw size={11}/> Refresh
        </button>
      </div>

      {/* Journeys table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {journeys.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">
            No journeys match. Start one from the Leads tab or via /api/nova/start.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-2.5">Lead</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Next touch</th>
                <th className="text-left px-4 py-2.5">Last touch</th>
                <th className="text-left px-4 py-2.5">Pref</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {journeys.map(j => {
                const touchNext = j.next_touch_index >= TOUCH_LABELS.length - 1
                  ? TOUCH_LABELS[TOUCH_LABELS.length - 1]
                  : TOUCH_LABELS[j.next_touch_index || 0];
                return (
                  <tr key={j.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-900 truncate">{j.lead_id}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mt-0.5">
                        {j.journey_kind === 'ppo_nurture' ? 'PPO nurture' : 'ACA nurture'}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">{statusChip(j.status, j.paused)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-700">
                      <span className="font-semibold">#{Math.min((j.next_touch_index || 0) + 1, TOUCH_LABELS.length)}</span> · {touchNext}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">
                      {fmtAgo(j.last_touch_at)}
                      {j.last_touch_channel && <span className="ml-1 text-slate-400">· {j.last_touch_channel}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">
                      {j.client_channel_pref || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => togglePause(j)}
                        className={`text-xs font-bold rounded-md px-2 py-1 inline-flex items-center gap-1 border ${
                          j.paused
                            ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                            : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                        }`}
                        title={j.paused ? 'Resume NOVA cadence' : 'Pause NOVA cadence for this lead'}
                      >
                        {j.paused ? <><Play size={10}/> Resume</> : <><Pause size={10}/> Pause</>}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiTile({ label, value, tone }) {
  const tones = {
    sky:     'bg-sky-500/20    text-sky-100    border-sky-400/30',
    emerald: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/30',
    amber:   'bg-amber-500/20  text-amber-100   border-amber-400/30',
    rose:    'bg-rose-500/30   text-rose-100    border-rose-400/40',
  };
  return (
    <div className={`rounded-lg px-3 py-2 border ${tones[tone] || tones.sky}`}>
      <div className="text-2xl font-black tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
    </div>
  );
}
